import type { Tournament, TournamentFormatConfig } from "./api";

export interface RuleListSection {
  key: string;
  title: string;
  items: string[];
}

export interface WeeklyRuleRow {
  week: number;
  label: string;
  bye: boolean;
}

export interface GeneratedRulesPresentation {
  sections: RuleListSection[];
  weeklyRows: WeeklyRuleRow[];
  weeklyTitle: string;
}

export function absoluteAssetUrl(
  path: string | null | undefined,
  apiBaseUrl: string,
  origin: string,
): string | null {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const appBase = apiBaseUrl.replace(/\/api\/v2$/, "").replace(/\/$/, "");
  const resolvedPath = `${appBase}${path.startsWith("/") ? path : `/${path}`}`;
  return new URL(resolvedPath, origin).href;
}

type ConfigRecord = Record<string, unknown>;

function asRecord(value: unknown): ConfigRecord {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as ConfigRecord)
    : {};
}

function asNumber(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function asString(value: unknown, fallback = ""): string {
  return typeof value === "string" && value.trim() ? value.trim() : fallback;
}

function cleanItems(items: Array<string | null | undefined>): string[] {
  return items.map((item) => (item ?? "").trim()).filter(Boolean);
}

function formatDate(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    return new Date(`${value}T00:00:00`).toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return value;
  }
}

function tournamentTypeLabel(type: string | null | undefined): string {
  switch (type) {
    case "ladder":
      return "Escalera";
    case "round_robin":
      return "Round Robin";
    case "elimination":
      return "Eliminación directa";
    default:
      return type || "Torneo";
  }
}

function setFormatLines(matchSetsFormat: string | undefined): string[] {
  switch (matchSetsFormat) {
    case "one_set":
      return ["1 set por partido.", "6-6: tiebreak normal a 7 puntos."];
    case "best_of_3":
      return ["2 de 3 sets completos.", "6-6: tiebreak normal a 7 puntos."];
    case "best_of_5":
      return ["3 de 5 sets completos.", "6-6: tiebreak normal a 7 puntos."];
    case "two_sets_super_tiebreak":
    default:
      return [
        "2 sets a 6 juegos.",
        "6-6: tiebreak normal a 7 puntos.",
        "1 set por lado: super tiebreak a 10 puntos.",
      ];
  }
}

function baseItems(
  tournament: Partial<Tournament> | null,
  formatType: string,
  config: ConfigRecord,
  defaultRoundCount: number,
): string[] {
  const roundCount = Math.max(1, asNumber(config.round_count, defaultRoundCount));
  const startDate = formatDate(asString(config.start_date, tournament?.start_date ?? ""));
  const endDate = formatDate(tournament?.end_date ?? null);
  const registrationDeadline = formatDate(tournament?.registration_deadline ?? null);
  const durationDays = asNumber(config.round_duration_days, 7);
  return cleanItems([
    `Sistema: ${tournamentTypeLabel(formatType)}.`,
    `${roundCount} jornada${roundCount === 1 ? "" : "s"}${durationDays ? ` de ${durationDays} día${durationDays === 1 ? "" : "s"}` : ""}.`,
    startDate ? `Inicio: ${startDate}.` : null,
    endDate ? `Cierre estimado: ${endDate}.` : null,
    registrationDeadline ? `Cierre de inscripciones: ${registrationDeadline}.` : null,
    tournament?.registration_cost ? `Costo de inscripción: ${tournament.registration_cost}.` : null,
    tournament?.official_ball ? `Pelota oficial: ${tournament.official_ball}.` : null,
    tournament?.venue_name ? `Sede: ${tournament.venue_name}.` : null,
  ]);
}

function weeklyPatternLabel(row: ConfigRecord): string {
  const pattern = asString(row.pattern ?? row.type, "manual");
  const offset = asNumber(row.offset ?? row.jump, 3);
  switch (pattern) {
    case "even_challenge_odd":
    case "even-challenges-odd":
      return "Pares retan a nones";
    case "odd_challenge_even":
    case "odd-challenges-even":
      return "Nones retan a pares";
    case "even_challenge_odd_offset":
    case "even-jumps-up":
      return `Pares retan ${offset} lugares arriba`;
    case "odd_challenge_even_offset":
    case "odd-jumps-up":
      return `Nones retan ${offset} lugares arriba`;
    case "adjacent":
      return "Por ranking (1° vs 2°, 3° vs 4°)";
    case "manual":
      return "Manual / definido por el admin";
    default:
      return pattern.replaceAll("_", " ");
  }
}

function defaultWeeklyRow(week: number): WeeklyRuleRow {
  if (week % 2 === 1) {
    return { week, label: "Pares retan 3 lugares arriba", bye: false };
  }
  return { week, label: "Nones retan 3 lugares arriba", bye: false };
}

function buildLadderRules(
  tournament: Partial<Tournament> | null,
  formatType: string,
  config: ConfigRecord,
): GeneratedRulesPresentation {
  const roundCount = Math.max(1, asNumber(config.round_count, 14));
  const matchSetsFormat = asString(config.match_sets_format, "two_sets_super_tiebreak");
  const playoffPlayers = asNumber(config.playoff_players_count, 6);
  const playoffRounds = asNumber(config.playoff_round_count, 0);
  const playoffSetsFormat = asString(config.playoff_sets_format, matchSetsFormat);

  const weeklyPatterns = Array.isArray(config.weekly_patterns)
    ? config.weekly_patterns
    : [];
  const weeklyRows: WeeklyRuleRow[] = Array.from({ length: roundCount }, (_, index) => {
    const week = index + 1;
    const row = asRecord(
      weeklyPatterns.find((entry) => asNumber(asRecord(entry).week, -1) === week),
    );
    if (!Object.keys(row).length) return defaultWeeklyRow(week);
    return {
      week,
      label: weeklyPatternLabel(row),
      bye: Boolean(row.bye),
    };
  });

  return {
    weeklyRows,
    weeklyTitle: `Sistema de retos (${roundCount} semana${roundCount === 1 ? "" : "s"})`,
    sections: [
      { key: "bases", title: "Bases", items: baseItems(tournament, formatType, config, 14) },
      {
        key: "formato",
        title: "Formato de juego",
        items: cleanItems([
          ...setFormatLines(matchSetsFormat),
          "Resultado no reportado dentro del plazo queda sujeto a revisión del admin.",
          playoffPlayers > 0 ? `Top ${playoffPlayers} clasifican a playoffs / Masters Finals.` : null,
          playoffRounds > 0 ? `${playoffRounds} jornada${playoffRounds === 1 ? "" : "s"} de playoffs.` : null,
          playoffRounds > 0 ? `Playoffs: ${setFormatLines(playoffSetsFormat)[0]}` : null,
        ]),
      },
      {
        key: "horarios",
        title: "Horarios y reportes",
        items: cleanItems([
          "Plazo para jugar: lunes a domingo de cada semana, salvo ajuste publicado por el admin.",
          "Acuerdo de horario: responsabilidad del retador contactar al retado.",
          "Reporte de resultado: antes del cierre semanal indicado por la organización.",
        ]),
      },
      {
        key: "clasificacion",
        title: "Clasificación y movimiento",
        items: cleanItems([
          "Ganador sube al lugar del perdedor cuando aplica por regla de escalera.",
          "BYE cuenta como victoria/descanso según el patrón de la jornada.",
          "Empates no permitidos; el super tiebreak decide el partido cuando corresponde.",
        ]),
      },
      {
        key: "conducta",
        title: "Conducta y bajas",
        items: conductItems(),
      },
    ],
  };
}

function buildRoundRobinRules(
  tournament: Partial<Tournament> | null,
  formatType: string,
  config: ConfigRecord,
): GeneratedRulesPresentation {
  const roundCount = Math.max(1, asNumber(config.round_count, 1));
  const matchSetsFormat = asString(config.match_sets_format, "two_sets_super_tiebreak");
  const playoffPlayers = asNumber(config.playoff_players_count, 0);
  const playoffRounds = asNumber(config.playoff_round_count, 0);
  const playoffSetsFormat = asString(config.playoff_sets_format, matchSetsFormat);
  const totalRows = roundCount + Math.max(0, playoffRounds);

  const weeklyRows: WeeklyRuleRow[] = Array.from({ length: totalRows }, (_, index) => {
    const week = index + 1;
    if (week <= roundCount) {
      return {
        week,
        label: "Partidos todos-contra-todos por grupo según calendario generado.",
        bye: false,
      };
    }
    const playoffIndex = week - roundCount;
    return {
      week,
      label: playoffIndex === playoffRounds ? "Final / cierre de playoffs." : "Ronda de playoffs.",
      bye: false,
    };
  });

  return {
    weeklyRows,
    weeklyTitle: `Calendario Round Robin (${totalRows} jornada${totalRows === 1 ? "" : "s"})`,
    sections: [
      { key: "bases", title: "Bases", items: baseItems(tournament, formatType, config, 1) },
      {
        key: "formato",
        title: "Formato de juego",
        items: cleanItems([
          "Round Robin por grupos: cada jugador se enfrenta contra los demás integrantes de su grupo.",
          "Cada partido regular suma para la tabla de su grupo y la clasificación general del torneo.",
          ...setFormatLines(matchSetsFormat),
          "Si un grupo tiene número impar de jugadores, puede existir descanso/BYE en alguna jornada.",
          playoffPlayers > 0 ? `Top ${playoffPlayers} de la clasificación avanzan a playoffs.` : null,
          playoffRounds > 0 ? `${playoffRounds} jornada${playoffRounds === 1 ? "" : "s"} de playoffs después de la fase de grupos.` : null,
          playoffRounds > 0 ? `Playoffs: ${setFormatLines(playoffSetsFormat)[0]}` : null,
        ]),
      },
      {
        key: "horarios",
        title: "Horarios y reportes",
        items: cleanItems([
          "Los partidos se juegan en las jornadas publicadas por el admin.",
          "Reporte de resultado: antes del cierre de la jornada indicada por la organización.",
          "Resultado no reportado dentro del plazo queda sujeto a revisión del admin.",
        ]),
      },
      {
        key: "clasificacion",
        title: "Clasificación y desempates",
        items: cleanItems([
          "La tabla se ordena por puntos: victoria 3, empate 1, derrota 0.",
          "Desempates: victorias, diferencia de games/puntos, games a favor y criterio del organizador.",
          playoffPlayers > 0 ? `Los ${playoffPlayers} mejores clasificados avanzan a la etapa de playoffs.` : null,
          "Empates no permitidos; el super tiebreak decide el partido cuando corresponde.",
        ]),
      },
      { key: "conducta", title: "Conducta y bajas", items: conductItems() },
    ],
  };
}

function buildEliminationRules(
  tournament: Partial<Tournament> | null,
  formatType: string,
  config: ConfigRecord,
): GeneratedRulesPresentation {
  const roundCount = Math.max(1, asNumber(config.round_count, 1));
  const matchSetsFormat = asString(config.match_sets_format, "two_sets_super_tiebreak");
  const weeklyRows: WeeklyRuleRow[] = Array.from({ length: roundCount }, (_, index) => ({
    week: index + 1,
    label: "Ronda de eliminación directa según bracket publicado.",
    bye: false,
  }));

  return {
    weeklyRows,
    weeklyTitle: `Bracket de eliminación (${roundCount} ronda${roundCount === 1 ? "" : "s"})`,
    sections: [
      { key: "bases", title: "Bases", items: baseItems(tournament, formatType, config, 1) },
      {
        key: "formato",
        title: "Formato de juego",
        items: cleanItems([
          "Eliminación directa: quien gana avanza a la siguiente ronda del bracket.",
          "El orden de partidos y cruces se define por el bracket publicado por el admin.",
          ...setFormatLines(matchSetsFormat),
          "Resultado no reportado dentro del plazo queda sujeto a revisión del admin.",
        ]),
      },
      {
        key: "horarios",
        title: "Horarios y reportes",
        items: cleanItems([
          "Los partidos se juegan en la fecha y horario publicados por el admin.",
          "Reporte de resultado: antes del cierre indicado por la organización.",
        ]),
      },
      {
        key: "clasificacion",
        title: "Avance en bracket",
        items: cleanItems([
          "El ganador avanza; el perdedor queda eliminado salvo partido de tercer lugar u otra regla publicada.",
          "Empates no permitidos; el super tiebreak decide el partido cuando corresponde.",
        ]),
      },
      { key: "conducta", title: "Conducta y bajas", items: conductItems() },
    ],
  };
}

function conductItems(): string[] {
  return cleanItems([
    "Puntualidad: tolerancia de 15 min; pasado ese tiempo se puede marcar W/O.",
    "Baja voluntaria: notificar al organizador con anticipación.",
    "Conducta antideportiva: sanción a criterio del organizador.",
    "Dudas y apelaciones: dirigirse al organizador antes del cierre semanal.",
  ]);
}

/** Solo la información del paso 1 (Datos): fechas, inscripción, costo,
 * pelota y sede. El Reglamento es el paso 2 y el Formato el paso 5 — el PDF
 * del reglamento NO puede derivar nada del formato porque en ese punto las
 * categorías ni siquiera lo tienen definido (decisión de Eduardo 2026-07-14). */
/** «Lo esencial» del reglamento del jugador (maqueta 2026-10-06): los mismos
 * datos de las Bases como mosaicos etiqueta/valor; solo los que existen. */
export function tournamentDataRuleTiles(
  tournament: Partial<Tournament> | null,
): Array<{ label: string; value: string }> {
  const tiles: Array<{ label: string; value: string | null | undefined }> = [
    { label: "Inicio", value: formatDate(tournament?.start_date ?? null) },
    { label: "Cierre estimado", value: formatDate(tournament?.end_date ?? null) },
    { label: "Cierre de inscripciones", value: formatDate(tournament?.registration_deadline ?? null) },
    { label: "Costo", value: tournament?.registration_cost },
    { label: "Pelota", value: tournament?.official_ball },
    { label: "Sede", value: tournament?.venue_name },
  ];
  return tiles
    .map((tile) => ({ label: tile.label, value: (tile.value ?? "").trim() }))
    .filter((tile) => tile.value);
}

export function tournamentDataRuleItems(tournament: Partial<Tournament> | null): string[] {
  const startDate = formatDate(tournament?.start_date ?? null);
  const endDate = formatDate(tournament?.end_date ?? null);
  const registrationDeadline = formatDate(tournament?.registration_deadline ?? null);
  return cleanItems([
    startDate ? `Inicio: ${startDate}.` : null,
    endDate ? `Cierre estimado: ${endDate}.` : null,
    registrationDeadline ? `Cierre de inscripciones: ${registrationDeadline}.` : null,
    tournament?.registration_cost ? `Costo de inscripción: ${tournament.registration_cost}.` : null,
    tournament?.official_ball ? `Pelota oficial: ${tournament.official_ball}.` : null,
    tournament?.venue_name ? `Sede: ${tournament.venue_name}.` : null,
  ]);
}

export function buildGeneratedRulesPresentation(
  tournament: Partial<Tournament> | null,
  formatConfig: TournamentFormatConfig | null | undefined,
): GeneratedRulesPresentation {
  // El format_config del torneo PADRE nace "ladder" al crearlo y NO se
  // re-sincroniza cuando las categorías definen su formato (solo
  // tournament.tournament_type se sincroniza desde las divisiones). Si ambos
  // discrepan, el config es un fósil de la creación: manda el tipo
  // sincronizado y se descartan sus números (round_count=8, etc. de escalera).
  const syncedType = tournament?.tournament_type ?? "";
  const configIsStale = Boolean(
    formatConfig?.format_type && syncedType && formatConfig.format_type !== syncedType,
  );
  const config = configIsStale ? {} : asRecord(formatConfig?.config_json);
  const formatType = configIsStale
    ? syncedType
    : formatConfig?.format_type || syncedType || "ladder";

  if (formatType === "round_robin") {
    return buildRoundRobinRules(tournament, formatType, config);
  }
  if (formatType === "elimination") {
    return buildEliminationRules(tournament, formatType, config);
  }
  return buildLadderRules(tournament, formatType, config);
}
